<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="car-crash-details-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Szczegóły szkody</h1>
      </div>
    </div>
    <!-- Add New Crash Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="dialogCreate" max-width="800px">
      <v-card>
        <v-card-title class="text-h5 primary--text">
          {{ $t('crashDetails.addHistory') }}
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="newCrash.crashId"
                  :label="$t('crashDetails.crashId')"
                  outlined
                  required
                  disabled
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="newCrash.description"
                  :label="$t('crashDetails.crashDescription')"
                  outlined
                  rows="3"
                />
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="newCrash.isVisibleToDriver"
                  :label="$t('crashDetails.visibleToDriver')"
                  color="primary"
                  hide-details
                >
                  <template v-slot:label>
                    <div>
                      <span>{{ $t('crashDetails.visibleToDriver') }}</span>
                      <div class="text-caption text--secondary">
                        {{ $t('crashDetails.visibleToDriverHint') }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-file-input
                  v-model="newCrash.files"
                  :label="$t('crashDetails.files')"
                  multiple
                  outlined
                  accept="image/*,.pdf,.doc,.docx"
                  @change="previewNewFiles"
                />
                <div v-if="newCrash.previews && newCrash.previews.length > 0" class="mt-3">
                  <h4>{{ $t('common.preview') }}:</h4>
                  <v-row>
                    <v-col v-for="(preview, index) in newCrash.previews" :key="index" cols="6" md="4">
                      <v-card>
                        <v-card-text class="text-center">
                          <img
                            v-if="preview.type === 'image'"
                            :src="preview.url"
                            style="max-width: 100%; max-height: 200px;"
                            alt="Preview"
                          />
                          <iframe
                            v-else-if="preview.type === 'pdf'"
                            :src="preview.url"
                            style="width: 100%; height: 200px"
                            frameborder="0"
                          >
                          </iframe>
                          <v-icon v-else size="60">mdi-file</v-icon>
                          <div class="mt-2">{{ preview.name }}</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="red" @click="closeCreateDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveCrash">{{ $t('common.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Content -->
    <v-row>
      <v-col cols="12">
        <!-- Page Header -->
        <v-card class="mb-4">
          <v-card-title class="text-h4 primary--text">
            <v-icon left large>mdi-car-crash</v-icon>
            {{ $t('crashDetails.title') }}
            <v-spacer />
            <v-btn color="primary" @click="dialogCreate = true">
              <v-icon left>mdi-plus</v-icon>
              {{ $t('crashDetails.addHistory') }}
            </v-btn>
          </v-card-title>
          <v-card-subtitle v-if="carInfo">
            Pojazd: {{ carInfo.make }} {{ carInfo.model }} - {{ carInfo.plate }}
          </v-card-subtitle>
        </v-card>

        <!-- Search and Filters -->
        <v-card class="mb-4">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="search"
                  :label="$t('crashDetails.searchHistory')"
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  clearable
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-btn @click="refreshData" color="primary" outlined>
                  <v-icon left>mdi-refresh</v-icon>
                  {{ $t('crashDetails.refreshData') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Kalkulator najmu pojazdu zastępczego -->
        <v-card class="mb-4 rent-calc">
          <v-card-title class="rent-calc-title">
            <v-icon left color="primary">mdi-calculator-variant</v-icon>
            {{ $t('crashes.rentCalculatorTitle') }}
          </v-card-title>
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="rentCalc.from"
                  type="date"
                  label="Od"
                  outlined dense hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="rentCalc.to"
                  type="date"
                  label="Do"
                  outlined dense hide-details
                  :min="rentCalc.from || undefined"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.number="rentCalc.dailyRate"
                  type="number"
                  min="0"
                  step="0.01"
                  label="Stawka za dobę (PLN)"
                  outlined dense hide-details
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model.number="rentCalc.vehicles"
                  type="number"
                  min="1"
                  step="1"
                  label="Liczba pojazdów"
                  outlined dense hide-details
                />
              </v-col>
            </v-row>

            <v-row dense class="mt-4">
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi">
                  <div class="rent-calc-kpi-label">{{ $t('crashes.dayCount') }}</div>
                  <div class="rent-calc-kpi-value">{{ rentDays }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi">
                  <div class="rent-calc-kpi-label">{{ $t('crashes.amountPerVehicle') }}</div>
                  <div class="rent-calc-kpi-value">{{ formatPln(rentPerVehicle) }}</div>
                  <div class="rent-calc-kpi-sub">{{ rentDays }} dni × {{ formatPln(rentCalc.dailyRate) }}</div>
                </div>
              </v-col>
              <v-col cols="12" md="4">
                <div class="rent-calc-kpi rent-calc-kpi--total">
                  <div class="rent-calc-kpi-label">Suma ({{ rentCalc.vehicles }} {{ rentCalc.vehicles === 1 ? 'pojazd' : 'pojazdów' }})</div>
                  <div class="rent-calc-kpi-value">{{ formatPln(rentTotal) }}</div>
                </div>
              </v-col>
            </v-row>

            <div v-if="rentError" class="rent-calc-error mt-3">
              <v-icon small color="error">mdi-alert-outline</v-icon>
              {{ rentError }}
            </div>

            <div class="rent-calc-actions mt-3">
              <v-btn small text @click="resetRentCalc">
                <v-icon small left>mdi-backup-restore</v-icon>{{ $t('crashes.clear') }}
              </v-btn>
              <v-btn small outlined color="primary" :disabled="!rentValid" @click="copyRentSummary">
                <v-icon small left>mdi-content-copy</v-icon>{{ $t('crashes.copySummary') }}
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <!-- Crash History Table -->
        <v-data-table
          :headers="headers"
          :items="crashHistory"
          class="elevation-1"
          :loading="loading"
          :search="search"
          :options.sync="options"
          :server-items-length="totalItems"
        >
          <template v-slot:top>
            <div class="table-section-title">
              <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-history</v-icon></div>
              <div class="filter-title">{{ $t('crashDetails.crashHistory') }}</div>
              <v-spacer></v-spacer>
            </div>
          </template>
          <template #item.createdAt="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>
          <template #item.type="{ item }">
            <v-chip color="orange" text-color="white" small>
              {{ item.type }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn
              small
              color="primary"
              @click="viewDetails(item)"
              class="mr-2"
            >
              <v-icon small>mdi-eye</v-icon>
              {{ $t('crashDetails.details') }}
            </v-btn>
            <v-btn
              v-if="item.files && item.files.length > 0"
              small
              color="secondary"
              @click="downloadFiles(item)"
            >
              <v-icon small>mdi-download</v-icon>
              {{ $t('crashDetails.files') }}
            </v-btn>
          </template>
          <template #no-data>
            <div class="text-center py-8">
              <v-icon size="64" color="grey">mdi-car-crash</v-icon>
              <div class="text-h6 grey--text mt-4">
                {{ $t('crashDetails.noCrashHistory') }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Details Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="detailsDialog" max-width="800px">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t('crashDetails.crashDetails') }}
        </v-card-title>
        <v-card-text v-if="selectedItem">
          <v-row>
            <v-col cols="12" md="6">
              <strong>ID Szkody:</strong> {{ selectedItem.crashId }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Data utworzenia:</strong> {{ formatDate(selectedItem.createdAt) }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Typ:</strong> {{ selectedItem.type }}
            </v-col>
            <v-col cols="12" md="6">
              <strong>Metadata:</strong> {{ selectedItem.metadata || 'Brak' }}
            </v-col>
            <v-col cols="12">
              <strong>Opis:</strong>
              <div class="mt-2">{{ selectedItem.description || 'Brak opisu' }}</div>
            </v-col>
            <v-col cols="12" v-if="selectedItem.files && selectedItem.files.length > 0">
              <strong>Dokumenty ({{ selectedItem.files.length }}):</strong>
              <div v-if="loadingPreviews" class="text-center my-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
                <div class="mt-2">{{ $t('common.loading') }}</div>
              </div>
              <v-row v-else class="mt-2">
                <v-col v-for="(file, index) in selectedItem.files" :key="file.id" cols="6" md="4">
                  <v-card outlined>
                    <v-card-text class="text-center pa-2">
                      <img
                        v-if="filePreviewUrls[index] && filePreviewUrls[index].type === 'image'"
                        :src="filePreviewUrls[index].url"
                        style="max-width: 100%; max-height: 200px; object-fit: contain; cursor: pointer;"
                        @click="openPreview(filePreviewUrls[index].url)"
                        alt="Podgląd"
                      />
                      <iframe
                        v-else-if="filePreviewUrls[index] && filePreviewUrls[index].type === 'pdf'"
                        :src="filePreviewUrls[index].url"
                        style="width: 100%; height: 200px"
                        frameborder="0"
                      ></iframe>
                      <v-icon v-else size="60" color="grey">mdi-file-document</v-icon>
                      <div class="mt-1 text-caption">{{ file.originalName || `Plik ${index + 1}` }}</div>
                    </v-card-text>
                    <v-card-actions class="justify-center pa-1">
                      <v-btn x-small color="primary" @click="downloadSingleFile(file.id)">
                        <v-icon x-small left>mdi-download</v-icon>
                        {{ $t('common.download') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn @click="detailsDialog = false">{{ $t('common.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
import * as dayjs from "dayjs";

export default {
  name: "CarCrashDetails",
  data() {
    return {
      loading: false,
      dialogCreate: false,
      detailsDialog: false,
      search: "",
      carId: null,
      carInfo: null,
      crashHistory: [],
      selectedItem: null,
      filePreviewUrls: [],
      loadingPreviews: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      rentCalc: {
        from: '',
        to: '',
        dailyRate: 150,
        vehicles: 1,
      },
      headers: [
        {
          text: "ID Szkody",
          align: "start",
          sortable: true,
          value: "crashId",
        },
        {
          text: "Data utworzenia",
          align: "start",
          sortable: true,
          value: "createdAt",
        },
        {
          text: "Typ",
          align: "start",
          sortable: true,
          value: "type",
        },
        {
          text: "Opis",
          align: "start",
          sortable: false,
          value: "description",
        },
        {
          text: "Akcje",
          align: "center",
          sortable: false,
          value: "actions",
        },
      ],
      newCrash: {
        crashId: "",
        description: "",
        metadata: {},
        files: [],
        previews: [],
        isVisibleToDriver: true, // Default to visible
      },
    };
  },
  watch: {
    options: {
      handler() {
        this.fetchCrashHistory();
      },
      deep: true,
    },
    search: {
      handler() {
        this.fetchCrashHistory();
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.filePreviewUrls.forEach(preview => {
      if (preview && preview.url) {
        window.URL.revokeObjectURL(preview.url);
      }
    });
  },
  async created() {
    this.carId = this.$route.query.id;
    if (!this.carId) {
      Swal.fire("Blad", "Brak ID pojazdu", "error");
      this.$router.push('/crashes');
      return;
    }

    // Set crashId to carId automatically
    this.newCrash.crashId = this.carId;

    // await this.fetchCarInfo();
    await this.fetchCrashHistory();
  },
  computed: {
    rentDays() {
      const from = this.rentCalc.from ? new Date(this.rentCalc.from) : null;
      const to = this.rentCalc.to ? new Date(this.rentCalc.to) : null;
      if (!from || !to || isNaN(from.getTime()) || isNaN(to.getTime())) return 0;
      const ms = to.getTime() - from.getTime();
      if (ms < 0) return 0;
      // Dodaj 1 dzień — najem liczy się od daty Od do daty Do włącznie
      const days = Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
      return Math.max(0, days);
    },
    rentPerVehicle() {
      const rate = Number(this.rentCalc.dailyRate) || 0;
      return this.rentDays * rate;
    },
    rentTotal() {
      const v = Math.max(1, Number(this.rentCalc.vehicles) || 1);
      return this.rentPerVehicle * v;
    },
    rentError() {
      if (!this.rentCalc.from || !this.rentCalc.to) return '';
      const from = new Date(this.rentCalc.from);
      const to = new Date(this.rentCalc.to);
      if (isNaN(from.getTime()) || isNaN(to.getTime())) return 'Nieprawidłowe daty';
      if (to < from) return 'Data "Do" nie może być wcześniejsza niż "Od"';
      if (Number(this.rentCalc.dailyRate) < 0) return 'Stawka nie może być ujemna';
      return '';
    },
    rentValid() {
      return this.rentDays > 0 && Number(this.rentCalc.dailyRate) > 0 && !this.rentError;
    },
  },
  methods: {
    resetRentCalc() {
      this.rentCalc = { from: '', to: '', dailyRate: 150, vehicles: 1 };
    },
    formatPln(v) {
      const n = Number(v) || 0;
      return n.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async copyRentSummary() {
      if (!this.rentValid) return;
      const lines = [
        `Najem pojazdu zastępczego`,
        `Okres: ${this.rentCalc.from} – ${this.rentCalc.to}`,
        `Liczba dni: ${this.rentDays}`,
        `Stawka dobowa: ${this.formatPln(this.rentCalc.dailyRate)}`,
        `Liczba pojazdów: ${this.rentCalc.vehicles}`,
        `Kwota za pojazd: ${this.formatPln(this.rentPerVehicle)}`,
        `SUMA: ${this.formatPln(this.rentTotal)}`,
      ];
      const text = lines.join('\n');
      try {
        await navigator.clipboard.writeText(text);
        Swal.fire({ icon: 'success', title: 'Skopiowano do schowka', timer: 1400, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Nie udało się skopiować', text, 'info');
      }
    },
    async toggleLoading(state) {
      this.loading = state;
      localStorage.setItem("loading", state);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },

    async fetchCarInfo() {
      try {
        const response = await axios({
          method: "get",
          url: `${process.env.VUE_APP_ROOT_API}cars/${this.carId}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.carInfo = response.data.data;
      } catch (e) {
        console.error(e);
        Swal.fire("Blad", "Nie udalo sie pobrac informacji o pojezdzie", "error");
      }
    },

    async fetchCrashHistory() {
      this.loading = true;
      if (this.search.trim().length > 0) {
        this.options.page = 1;
      }
      try {
        const response = await axios({
          method: "get",
          url: `${process.env.VUE_APP_ROOT_API}cars-history/crash-history/${this.carId}?take=${this.options.itemsPerPage}&skip=${this.options.page}&search=${this.search}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.crashHistory = response.data.data.result || [];
        this.totalItems = response.data.data.count || 0;
      } catch (e) {
        console.error(e);
        if (e.response?.status === 404) {
          this.crashHistory = [];
          this.totalItems = 0;
        } else {
          Swal.fire("Blad", "Nie udalo sie pobrac historii szkod", "error");
        }
      }
      this.loading = false;
    },

    async saveCrash() {
      if (!this.newCrash.crashId) {
        Swal.fire("Blad", "ID szkody jest wymagane", "warning");
        return;
      }

      await this.toggleLoading(true);
      try {
        const formData = new FormData();

        // Add files to FormData
        this.newCrash.files?.forEach((file) => {
          try {
            formData.append(`file`, file, file.name);
          } catch (e) {
            console.log("Error adding file:", e);
          }
        });

        // Add other data
        formData.append("crashId", this.newCrash.crashId);
        formData.append("description", this.newCrash.description || "");
        formData.append("metadata", JSON.stringify(this.newCrash.metadata || {}));
        formData.append("isVisibleToDriver", this.newCrash.isVisibleToDriver);

        await axios({
          method: "post",
          url: `${process.env.VUE_APP_ROOT_API}cars-history/crash-history`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });

        Swal.fire("Sukces", "Szkoda zostala dodana", "success");
        this.closeCreateDialog();
        await this.fetchCrashHistory();
      } catch (e) {
        console.error(e);
        Swal.fire("Blad", e.response?.data?.message || "Nie udalo sie dodac szkody", "error");
      }
      await this.toggleLoading(false);
    },

    closeCreateDialog() {
      this.dialogCreate = false;
      this.newCrash = {
        crashId: this.carId, // Reset to carId
        description: "",
        metadata: {},
        files: [],
        previews: [],
        isVisibleToDriver: true, // Reset to default visible
      };
    },

    previewNewFiles(files) {
      this.newCrash.previews = [];
      if (files && files.length > 0) {
        files.forEach((file) => {
          if (file instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
              const preview = {
                name: file.name,
                url: e.target.result,
                type: file.type.startsWith('image/') ? 'image' :
                      file.type === 'application/pdf' ? 'pdf' : 'other'
              };
              this.newCrash.previews.push(preview);
            };
            reader.readAsDataURL(file);
          }
        });
      }
    },

    viewDetails(item) {
      this.selectedItem = item;
      this.filePreviewUrls = [];
      this.detailsDialog = true;
      if (item.files && item.files.length > 0) {
        this.loadFilePreviews(item.files);
      }
    },

    openPreview(url) {
      if (url) window.open(url, '_blank');
    },

    async loadFilePreviews(files) {
      this.loadingPreviews = true;
      this.filePreviewUrls = [];
      try {
        for (const file of files) {
          try {
            const response = await axios({
              method: "post",
              url: `${process.env.VUE_APP_ROOT_API}aws/download-file/${file.id}`,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
              responseType: "blob",
            });
            const blob = new Blob([response.data], { type: response.data.type });
            const url = window.URL.createObjectURL(blob);
            const type = response.data.type.startsWith('image/') ? 'image' :
                         response.data.type === 'application/pdf' ? 'pdf' : 'other';
            this.filePreviewUrls.push({ url, type });
          } catch (e) {
            console.error('Failed to load preview for file:', file.id, e);
            this.filePreviewUrls.push({ url: null, type: 'other' });
          }
        }
      } finally {
        this.loadingPreviews = false;
      }
    },

    async downloadFiles(item) {
      if (!item.files || item.files.length === 0) {
        Swal.fire("Informacja", "Brak plikow do pobrania", "info");
        return;
      }

      await this.toggleLoading(true);
      try {
        for (const file of item.files) {
          await this.downloadSingleFile(file.id);
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Blad", "Nie udalo sie pobrac plikow", "error");
      }
      await this.toggleLoading(false);
    },

    async downloadSingleFile(fileId) {
      try {
        const response = await axios({
          method: "post",
          url: `${process.env.VUE_APP_ROOT_API}aws/download-file/${fileId}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });

        const blob = new Blob([response.data], { type: response.data.type });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      } catch (e) {
        console.error(e);
        Swal.fire("Blad", "Nie udalo sie pobrac pliku", "error");
      }
    },

    async refreshData() {
      await this.fetchCrashHistory();
      Swal.fire("Sukces", "Dane zostaly odswiezione", "success");
    },

    formatDate(dateString) {
      if (!dateString) return "Brak daty";
      return dayjs(dateString).format("DD.MM.YYYY HH:mm");
    },
  },
};
</script>

<style scoped>
.car-crash-details-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.car-crash-details-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.car-crash-details-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.car-crash-details-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.car-crash-details-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.car-crash-details-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.car-crash-details-page >>> .v-text-field input,
.car-crash-details-page >>> .v-text-field textarea,
.car-crash-details-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.car-crash-details-page >>> .v-label {
  color: #94A0B8 !important;
}
.car-crash-details-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.car-crash-details-page >>> .v-card__title,
.car-crash-details-page >>> .v-card__text {
  color: #E6EAF2 !important;
}
.car-crash-details-page >>> .primary--text {
  color: #5478FF !important;
}
.v-card {
  border-radius: 12px;
}

.v-btn {
  border-radius: 8px;
}

.v-chip {
  font-weight: bold;
}

/* Kalkulator najmu */
.rent-calc-title {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .2px;
}
.rent-calc-kpi {
  background: #f5f7fb;
  border: 1px solid #e2e5ea;
  border-radius: 10px;
  padding: 14px 16px;
  text-align: center;
}
.rent-calc-kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0,0,0,.6);
  text-transform: uppercase;
  letter-spacing: .5px;
}
.rent-calc-kpi-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
}
.rent-calc-kpi-sub {
  font-size: 11px;
  color: #888;
  margin-top: 2px;
}
.rent-calc-kpi--total {
  background: #eef4ff;
  border-color: #b9cff9;
}
.rent-calc-kpi--total .rent-calc-kpi-value { color: #1e40af; }
.rent-calc-error {
  color: #c62828;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.rent-calc-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>